/**
 * AI Manager API Service
 *
 * Connects SLIK-POS to the SLIK AI Manager API server.
 * Provides real-time AI-powered insights, coaching, and analytics.
 *
 * API Server: http://localhost:8000 (slik-ai-manager)
 */

import { isLocalApiUnavailable } from '../utils/devApi';

const AI_MANAGER_BASE_URL = process.env.REACT_APP_AI_MANAGER_URL || 'http://localhost:8000';

// =============================================================================
// TYPES
// =============================================================================

export interface CPIResponse {
  client_id: string;
  client_name: string;
  cpi_score: number;
  tier: 'PLATINUM' | 'GOLD' | 'SILVER' | 'BRONZE';
  components: {
    visit_score: number;
    spend_score: number;
    retention_score: number;
    referral_score: number;
    tip_score: number;
  };
  raw_data: {
    total_visits: number;
    total_spent: number;
    days_as_client: number;
    referrals: number;
    avg_tip: number;
  };
  recommendation: string;
}

export interface BPIResponse {
  barber_id: string;
  barber_name: string;
  period: 'daily' | 'weekly' | 'monthly';
  bpi_score: number;
  status: 'EXCELLENT' | 'GOOD' | 'DEVELOPING' | 'NEEDS ATTENTION';
  components: {
    service_score: number;
    revenue_score: number;
    speed_score: number;
    satisfaction_score: number;
    rebooking_score: number;
  };
  raw_data: {
    services_completed: number;
    total_revenue: number;
    avg_service_time: number;
    client_satisfaction: number;
    rebooking_rate: number;
  };
  action: string;
}

export interface FraudResponse {
  barber_id: string;
  barber_name: string;
  period_days: number;
  risk_level: 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';
  risk_score: number;
  flags: Array<{
    check: string;
    detail: string;
    risk_points: number;
  }>;
  recommendation: string;
  summary: {
    transactions_analyzed: number;
    flags_triggered: number;
  };
}

export interface NudgeResponse {
  barber_id: string;
  barber_name: string;
  nudge_type: string;
  message: string;
  data?: Record<string, any>;
}

export interface PartnerJourneyResponse {
  barber_id: string;
  barber_name: string;
  current_stage: string;
  next_stage: string | null;
  equity_percent: number;
  progress: {
    experience: number;
    revenue: number;
    rating: number;
  };
  overall_progress: number;
  current_stats: {
    experience_years: number;
    monthly_revenue: number;
    avg_rating: number;
  };
  gaps: string[];
  recommendation: string;
}

export interface ChatResponse {
  response: string;
  context_used: string[];
  model_used: string;
}

export interface ShopTodayResponse {
  date: string;
  shop_revenue: {
    total: number;
    goal: number;
    completed_services: number;
    pending_services: number;
  };
  barber_stats: Array<{
    id: string;
    name: string;
    revenue_today: number;
    completed_today: number;
    remaining_today: number;
  }>;
}

export interface BarberListResponse {
  count: number;
  barbers: Array<{
    id: string;
    name: string;
    role: string;
    status: string;
    careerStage: string;
  }>;
}

export interface AIManagerStatus {
  status: string;
  version: string;
  models: {
    claude?: {
      available: boolean;
      model: string;
    };
    openai: {
      available: boolean;
      model: string;
      fallback: string;
    };
    ollama: {
      available: boolean | null;
      model: string;
      base_url: string;
    };
    prefer_local: boolean;
    fallback_on_error: boolean;
  };
  firebase: {
    connected: boolean;
    mode: 'live' | 'mock';
  };
}

// =============================================================================
// API SERVICE CLASS
// =============================================================================

class AIManagerAPIService {
  private baseUrl: string;
  private isAvailable: boolean | null = null;

  constructor(baseUrl: string = AI_MANAGER_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Check if the AI Manager API is available
   */
  async checkHealth(): Promise<boolean> {
    // On the deployed site the localhost dev API is unreachable — don't fire a doomed request.
    if (isLocalApiUnavailable(this.baseUrl)) { this.isAvailable = false; return false; }
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      this.isAvailable = response.ok;
      return this.isAvailable;
    } catch (error) {
      console.warn('AI Manager API not available:', error);
      this.isAvailable = false;
      return false;
    }
  }

  /**
   * Get AI Manager status (models, Firebase connection)
   */
  async getStatus(): Promise<AIManagerStatus | null> {
    try {
      const response = await fetch(`${this.baseUrl}/status`);
      if (!response.ok) throw new Error('Failed to get status');
      return await response.json();
    } catch (error) {
      console.error('Error getting AI Manager status:', error);
      return null;
    }
  }

  /**
   * Calculate Client Performance Index
   */
  async calculateCPI(clientId: string): Promise<CPIResponse | null> {
    if (isLocalApiUnavailable(this.baseUrl)) return null; // deployed site: no local AI manager
    try {
      const response = await fetch(`${this.baseUrl}/api/cpi/${clientId}`);
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error('Failed to calculate CPI');
      }
      return await response.json();
    } catch (error) {
      console.error('Error calculating CPI:', error);
      return null;
    }
  }

  /**
   * Calculate Barber Performance Index
   */
  async calculateBPI(
    barberId: string,
    period: 'daily' | 'weekly' | 'monthly' = 'monthly'
  ): Promise<BPIResponse | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/bpi/${barberId}?period=${period}`);
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error('Failed to calculate BPI');
      }
      return await response.json();
    } catch (error) {
      console.error('Error calculating BPI:', error);
      return null;
    }
  }

  /**
   * Detect fraud patterns for a barber
   */
  async detectFraud(barberId: string, days: number = 30): Promise<FraudResponse | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/fraud/${barberId}?days=${days}`);
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error('Failed to detect fraud');
      }
      return await response.json();
    } catch (error) {
      console.error('Error detecting fraud:', error);
      return null;
    }
  }

  /**
   * Generate a coaching nudge for a barber
   */
  async generateNudge(
    barberId: string,
    nudgeType: 'performance' | 'revenue' | 'booking' | 'client' = 'performance',
    context?: string
  ): Promise<NudgeResponse | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/nudge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          barber_id: barberId,
          nudge_type: nudgeType,
          context,
        }),
      });
      if (!response.ok) throw new Error('Failed to generate nudge');
      return await response.json();
    } catch (error) {
      console.error('Error generating nudge:', error);
      return null;
    }
  }

  /**
   * NARRATE the coach's development plan in a mentor voice — the AI Practice Coach's SERVICE
   * layer on top of the deterministic framework (shared/coach.ts).
   *
   * This is the ONE model-agnostic contract: send the FRAMEWORK's already-computed plan →
   * receive a short, human, encouraging paragraph. The model BEHIND /api/coach/narrate is a
   * SWAPPABLE choice and the app never changes when you swap it:
   *   • a LOCAL model (Ollama / MLX / llama.cpp) running on your own hardware, OR
   *   • a cloud API (Anthropic Claude or OpenAI).
   * Returns null whenever no backend is reachable → the app shows the STRUCTURED plan and the
   * framework stands entirely on its own. The AI is purely additive.
   */
  async narrateCoachingPlan(payload: {
    barberId?: string;
    barberName?: string;
    ppi: number;
    level: string;
    priorities: Array<{ label: string; current: number; desired: number; actions: string[] }>;
    strengths: string[];
    signals: string[];
  }): Promise<{ message: string } | null> {
    if (isLocalApiUnavailable(this.baseUrl)) return null;   // deployed site w/ no backend → framework-only
    try {
      const response = await fetch(`${this.baseUrl}/api/coach/narrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * Get partner journey assessment for a barber
   */
  async getPartnerJourney(barberId: string): Promise<PartnerJourneyResponse | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/partner/${barberId}`);
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error('Failed to get partner journey');
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting partner journey:', error);
      return null;
    }
  }

  /**
   * AI chat with shop context
   */
  async chat(
    message: string,
    options?: {
      barberId?: string;
      clientId?: string;
      context?: string;
    }
  ): Promise<ChatResponse | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          barber_id: options?.barberId,
          client_id: options?.clientId,
          context: options?.context,
        }),
      });
      if (!response.ok) throw new Error('Failed to chat');
      return await response.json();
    } catch (error) {
      console.error('Error in AI chat:', error);
      return null;
    }
  }

  /**
   * Get today's shop overview
   */
  async getShopToday(): Promise<ShopTodayResponse | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/shop/today`);
      if (!response.ok) throw new Error('Failed to get shop overview');
      return await response.json();
    } catch (error) {
      console.error('Error getting shop overview:', error);
      return null;
    }
  }

  /**
   * List all barbers
   */
  async listBarbers(): Promise<BarberListResponse | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/barbers`);
      if (!response.ok) throw new Error('Failed to list barbers');
      return await response.json();
    } catch (error) {
      console.error('Error listing barbers:', error);
      return null;
    }
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

export const aiManagerAPI = new AIManagerAPIService();

export default aiManagerAPI;
